import type {
  ChildGuideItem,
  FollowAction,
  FollowOption,
} from '@tunein/web-api-client';
import type { Metadata } from '@tunein/web-player-manager';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { apiBeta } from 'src/client/api';
import { player } from 'src/client/player';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { promptUserForAuth } from 'src/common/actions/auth';
import { showMessage } from 'src/common/actions/messagePopover';
import { useStores } from 'src/common/providers';
import { selectIsAuthenticated } from 'src/common/selectors/auth';
import { selectIsUserSubscribed } from 'src/common/selectors/me';
import { getFavoriteMessageText } from 'src/common/utils/getFavoriteMessageText';

interface UseFavoriteOptions {
  guideItem?: ChildGuideItem;
  // Action is from Guide Item, Option is from Metadata (Now Playing)
  followActionOrOption?: FollowAction & FollowOption;
}

// TODO: the switch action can logic can be removed once this Platform ticket is resolved that fixes the guide id in the
//  NP follow action for premium guide ids: https://tunein.atlassian.net/browse/PLATFORM-19553
function getFollowOptions(
  guideItem?: ChildGuideItem,
  followActionOrOption?: FollowAction & FollowOption,
  metadata?: Metadata | null,
  isPremium?: boolean,
) {
  const {
    canFollow = true,
    isFollowing,
    guideId = '',
  } = guideItem?.actions?.follow || followActionOrOption || {};
  const {
    primaryGuideId,
    followOptions: [followOption] = [],
    switchAction,
    isBoostLoaded,
    boost,
  } = metadata || {};

  return {
    canFollow,
    isFollowing: isFollowing ?? followOption?.isFollowing,
    guideId:
      guideId ||
      (isBoostLoaded && boost?.primary?.guideId) ||
      (isPremium ? switchAction?.destinationGuideId : followOption?.guideId) ||
      primaryGuideId,
  };
}

export function useFavorite(options: UseFavoriteOptions) {
  const { guideItem, followActionOrOption } = options;
  const { playerStore, favoritesStore } = useStores();
  const metadata = playerStore.metadata();
  const isPremium = useSelector(selectIsUserSubscribed);
  const {
    canFollow = true,
    isFollowing,
    guideId = '',
  } = getFollowOptions(guideItem, followActionOrOption, metadata, isPremium);
  const registerButton = favoritesStore.registerButton();
  const unregisterButton = favoritesStore.unregisterButton();
  const toggleFavoriteInStore = favoritesStore.toggle();
  const unlockGuideId = favoritesStore.unlock();
  const favorites = favoritesStore.favorites();
  const setFavoriteOnAuthCallback = favoritesStore.setFavoriteOnAuthCallback();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  /**
   * Registers the favorite button for client-side favorite state management for the guide id. More than 1 button can
   * register to the same guide id. Button registration persists favorite state for a guide id until all buttons for
   * that guide id are unregistered. In addition to synchronizing favorite states across buttons (e.g., profile header
   * and mini player), this mechanism also ensures that client-side favorite states are short-lived.
   */
  useEffect(() => {
    if (!(isAuthenticated && guideId)) {
      return;
    }

    registerButton(guideId, isFollowing);

    return () => {
      unregisterButton(guideId);
    };
  }, [isAuthenticated, guideId, isFollowing, registerButton, unregisterButton]);

  /**
   * Toggling favorite state updates the client-side state, posts the change to the API, surfaces a message toast, and
   * refreshes metadata (this is needed to update favorite labels on popover menus)
   */
  const toggleFavorite = useCallback(async () => {
    // This should never happen, but just in case
    if (!(guideId && favorites[guideId])) {
      return;
    }

    // toggling "locks" favoriting for guide id, signaling button to be disabled
    toggleFavoriteInStore(guideId);

    const { favorited, unFavorited, favoriteError, unFavoriteError } =
      getFavoriteMessageText(guideId, storeSingleton.state);
    const nextIsFavorited = !favorites[guideId].isFavorited;

    try {
      await apiBeta.user.toggleFavorite({
        guideId,
        favorite: nextIsFavorited,
      });

      storeSingleton.dispatch(
        showMessage(nextIsFavorited ? favorited : unFavorited),
      );
    } catch {
      toggleFavoriteInStore(guideId);

      storeSingleton.dispatch(
        showMessage(nextIsFavorited ? favoriteError : unFavoriteError),
      );
    }

    if (player.isActive) {
      player.fetchMetadata();
    }

    // when favoriting is done, unlock favoriting for guide id
    unlockGuideId(guideId);
  }, [guideId, toggleFavoriteInStore, favorites, unlockGuideId]);

  /**
   * When an anon user tries to favorite a guide, prompt them to auth, and cache a callback to save their favorite
   * preference after they've successfully authed. The cached callback is executed by the useAuthEffects() hook.
   */
  function handleFavoriteForAnonUser() {
    promptUserForAuth();

    setFavoriteOnAuthCallback(async () => {
      try {
        await apiBeta.user.toggleFavorite({ guideId, favorite: true });

        // enforcing positive favoriting with second arg
        toggleFavoriteInStore(guideId, true);

        storeSingleton.dispatch(
          showMessage(
            getFavoriteMessageText(guideId, storeSingleton.state)?.favorited,
          ),
        );

        if (player.isActive) {
          player.fetchMetadata();
        }
      } catch {
        // do nothing
      }
    });
  }

  const { isLocked, isFavorited } = favorites[guideId] || {};

  return {
    guideId,
    canFollow,
    isLocked,
    isFavorited,
    toggleFavorite: isAuthenticated
      ? toggleFavorite
      : handleFavoriteForAnonUser,
  };
}
